import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { MapPin, Heart, Stethoscope, X } from "lucide-react";
import { toast } from "sonner";
import { isFav, toggleFav } from "../utils/favourites";

export default function PetDetailModal({ pet, open, onOpenChange }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [fav, setFav] = useState(false);

  useEffect(() => {
    if (pet) setFav(isFav(pet.id));
  }, [pet]);

  if (!pet) return null;

  const statusLabel =
    pet.status === "fostered" ? "Fostered" : "Needs home";

  const submit = (e) => {
    e.preventDefault();
    toast.success(`Thanks ${name || "friend"}! Your interest in ${pet.name} was sent \uD83D\uDC3E`);
    setShowForm(false);
    setName("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden rounded-3xl max-w-[400px] gap-0 border-0">
        <div className="relative">
          <img loading="lazy" decoding="async" src={pet.image} alt={pet.name} className="w-full h-64 object-cover" />
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 bg-black/40 backdrop-blur text-white rounded-full p-1.5"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={() => { const n = toggleFav(pet.id); setFav(n.includes(pet.id)); }}
            className="absolute top-3 right-12 bg-black/40 backdrop-blur rounded-full p-1.5"
          >
            <Heart className={`w-4 h-4 ${fav ? "text-red-500" : "text-white"}`} fill={fav ? "currentColor" : "none"} />
          </button>
          <div className="absolute top-3 left-3 flex gap-2">
            <span className="bg-white/90 text-slate-900 text-xs font-extrabold px-2.5 py-1 rounded-full">
              {pet.speciesEmoji} {pet.species}
            </span>
            {pet.vet && (
              <span className="bg-red-600 text-white text-xs font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Stethoscope className="w-3 h-3" /> VET
              </span>
            )}
          </div>
          <span className="absolute bottom-3 left-3 bg-[var(--paw-orange)] text-white text-xs font-extrabold px-2.5 py-1 rounded-full">
            {statusLabel}
          </span>
        </div>

        <div className="p-5">
          <h2 className="font-head text-2xl font-extrabold text-slate-900">{pet.name}</h2>
          <div className="flex items-center gap-1 text-slate-500 text-sm font-semibold mt-0.5">
            <MapPin className="w-4 h-4" /> {pet.suburb} · {pet.breed}
          </div>
          <p className="text-slate-600 mt-3 leading-relaxed">{pet.description}</p>
          <div className="mt-3 inline-block bg-emerald-50 text-emerald-700 font-extrabold text-sm px-3 py-1 rounded-full">
            {pet.fee}
          </div>

          {!showForm ? (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full mt-5 h-12 rounded-2xl bg-[var(--paw-orange)] hover:bg-[var(--paw-orange-dark)] text-white font-extrabold text-base"
            >
              <Heart className="w-5 h-5 mr-1" fill="currentColor" /> Meet {pet.name}
            </Button>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-3">
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-12 rounded-2xl"
              />
              <Input required type="tel" placeholder="Your phone" className="h-12 rounded-2xl" />
              <Textarea placeholder={`Tell us why you'd be a great home for ${pet.name}`} className="rounded-2xl" />
              <Button
                type="submit"
                className="w-full h-12 rounded-2xl bg-[var(--paw-orange)] hover:bg-[var(--paw-orange-dark)] text-white font-extrabold"
              >
                Send my interest
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
